import React, { useEffect, useMemo, useState } from "react";

import {
	FrontendBatchType,
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
	SysAdminCompanyTestingWorkflowWithBatchTypeIds,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Severity } from "node-library/dist/constants";
import { GreenButton } from "../../../../common-components/button";
import { CustomSearchBar } from "../../../../common-components/inputs";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../requestHelper/sendRequest";
import AddOrWditWorkflowBasicInfoModal from "../components/addOrEditWorkflowBasicInfoModal";
import WorkflowCell from "../components/workflowCell";
import WorkflowStructureDetailsDrawer from "../components/workflowStructureDetailsDrawer";

interface Props {
	user: FrontendUserInfo;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
}

export default function WorkflowStructureTab(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { user, company, setCompany } = props;

	// const hasCategoryToDisplay: boolean =
	// 	company.companyTestCategories.length > 0;
	// const [selectedTestCategory, setSelectedTestCategory] = useState<
	// 	FrontendCompanyTestCategory | undefined
	// >(hasCategoryToDisplay ? company.companyTestCategories[0] : undefined);

	const [searchText, setSearchText] = useState<string>("");

	const [workflowOverviews, setWorkflowOverviews] = useState<
		Array<SysAdminCompanyTestingWorkflowWithBatchTypeIds>
	>([]);

	const [filteredWorkflows, setFilteredWorkflows] = useState<
		Array<SysAdminCompanyTestingWorkflowWithBatchTypeIds>
	>([]);

	const [selectedWorkflow, setSelectedWorkflow] = useState<
		SysAdminCompanyTestingWorkflowWithBatchTypeIds | undefined
	>();

	const [addWorkflowModalOpen, setAddWorkflowModalOpen] =
		useState<boolean>(false);
	const [workflowDetailsDrawerOpen, setWorkflowDetailsDrawerOpen] =
		useState<boolean>(false);

	const [overviewLoaded, setOverviewLoaded] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const companyBatchType: Array<FrontendBatchType> = useMemo(() => {
		const batchTypeIds: Set<string> = new Set();
		const result: Array<FrontendBatchType> = [];

		for (const testCategory of company.companyTestCategories) {
			for (const batchType of testCategory.batchTypes) {
				if (batchTypeIds.has(batchType.batchTypeId)) {
					continue;
				}

				result.push(batchType);
				batchTypeIds.add(batchType.batchTypeId);
			}
		}

		return result;
	}, [company.companyTestCategories]);

	function filterResults() {
		// 1. If there's no search text, just show everything from the category
		if (!searchText.trim()) {
			setFilteredWorkflows(workflowOverviews);
			return;
		}

		const lowerSearchText = searchText.toLowerCase();

		// 2. Perform the filter
		const filtered = workflowOverviews.filter((overview) => {
			// Check if the Field Name matches
			const nameMatches = overview.workflowName
				.toLowerCase()
				.includes(lowerSearchText);

			return nameMatches;
		});

		setFilteredWorkflows(filtered);
	}

	async function getWorkflowOverview() {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/structures/get-company-workflow-overviews",
				{},
			);
			const workflowOverviews: Array<SysAdminCompanyTestingWorkflowWithBatchTypeIds> =
				response?.data?.workflowOverviews;
			setWorkflowOverviews(workflowOverviews);

			setOverviewLoaded(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		filterResults();
	}, [workflowOverviews]);

	useEffect(() => {
		if (!overviewLoaded) {
			getWorkflowOverview();
		}
	}, []);
	const workflowFilterBar: React.ReactNode = (
		<CustomSearchBar
			id="filter-result-searchbar"
			searchText={searchText}
			setSearchText={setSearchText}
			placeholder={translation("Search")}
			searchButtonText={translation("Search")}
			onSearch={() => {
				filterResults();
			}}
		></CustomSearchBar>
	);

	const createWorkflowButton: React.ReactNode = (
		<GreenButton
			key="create-result-field-button"
			id="create-result-field-button"
			size="flex"
			children={
				<>
					<AddIcon></AddIcon>
					{translation("Workflow")}
				</>
			}
			onClick={() => {
				setSelectedWorkflow(undefined);
				setAddWorkflowModalOpen(true);
			}}
		></GreenButton>
	);

	// Inside ResultStructureTab.tsx
	const workflowCells: Array<React.ReactNode> = filteredWorkflows
		? filteredWorkflows.map(
				(workflow: SysAdminCompanyTestingWorkflowWithBatchTypeIds) => {
					const batchTypeNames: Array<string> = companyBatchType
						.filter((batchType) => {
							return workflow.batchTypeIds.includes(batchType.batchTypeId);
						})
						.map((batchType) => {
							return `${batchType.batchName}`;
						});

					return (
						<WorkflowCell
							key={workflow.workflowId} // Always use a unique key
							workflow={workflow}
							batchTypes={batchTypeNames}
							goToViewWorkflowDetails={() => {
								setSelectedWorkflow(workflow);
								setWorkflowDetailsDrawerOpen(true);
							}}
						/>
					);
				},
			)
		: [];

	async function addOrEditWorkflow(
		workflow: SysAdminCompanyTestingWorkflowWithBatchTypeIds,
		isEdit: boolean,
	) {
		try {
			let response;
			setLoading(true);
			if (isEdit) {
				response = await sendPOSTRequest(
					"/structures/update-workflow-basic-info",
					{
						updatedWorkflow: workflow,
					},
				);

				setWorkflowOverviews(
					workflowOverviews.map((prev) => {
						return workflow.workflowId === prev.workflowId ? workflow : prev;
					}),
				);
			} else {
				response = await sendPOSTRequest("/structures/create-new-workflow", {
					newWorkflow: workflow,
				});
				const workflowId: string = response.newWorkflowId;

				const newWorkflowOverview: SysAdminCompanyTestingWorkflowWithBatchTypeIds =
					{
						...workflow,
						workflowId: workflowId,
					};

				setWorkflowOverviews([...workflowOverviews, newWorkflowOverview]);
			}

			openSnackbar(
				translation("ResultFieldUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (error: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	return (
		<>
			<>
				<div className={`batch-details-workflow-container`}>
					<div>{createWorkflowButton}</div>
					<div>{workflowFilterBar}</div>

					{filteredWorkflows.length > 0 ? (
						<div className="view-order-grid-table">{workflowCells}</div>
					) : (
						<div>{translation("NoWorkflows")}</div>
					)}
				</div>
			</>

			<AddOrWditWorkflowBasicInfoModal
				company={company}
				companyBatchType={companyBatchType}
				addOrEditWorkflow={addOrEditWorkflow}
				open={addWorkflowModalOpen}
				setOpen={setAddWorkflowModalOpen}
				loading={loading}
			></AddOrWditWorkflowBasicInfoModal>
			{selectedWorkflow && (
				<WorkflowStructureDetailsDrawer
					company={company}
					setCompany={setCompany}
					user={user}
					open={workflowDetailsDrawerOpen}
					setOpen={setWorkflowDetailsDrawerOpen}
					workflow={
						selectedWorkflow as SysAdminCompanyTestingWorkflowWithBatchTypeIds
					}
					batchTypes={
						selectedWorkflow
							? companyBatchType
									.filter((batchType) => {
										return selectedWorkflow.batchTypeIds.includes(
											batchType.batchTypeId,
										);
									})
									.map((batchType) => {
										return `${batchType.batchName}`;
									})
							: []
					}
				></WorkflowStructureDetailsDrawer>
			)}
		</>
	);
}
